import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    TextField, Stack, IconButton, Alert, useTheme, ToggleButton, ToggleButtonGroup,
    Select, MenuItem
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LogoutIcon from '@mui/icons-material/Logout';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const MODE_SINGLE = 'SINGLE';
const MODE_RANGE = 'RANGE';

const EVENT_TYPE_ARRIVAL = 'ARRIVAL';
const EVENT_TYPE_DEPARTURE = 'DEPARTURE';
const EVENT_TYPE_BREAK_START = 'BREAK_START';
const EVENT_TYPE_BREAK_END = 'BREAK_END';
const EVENT_TYPE_FULL_SHIFT = 'FULL_SHIFT';
const EVENT_TYPE_BREAK_DURATION = 'BREAK_DURATION';

const AddEditRecordDialog = ({ open, onClose, onSave, selectedDate = dayjs(), editingEvent = null, hasActiveRecord = false, hasActiveBreak = false, hasAnyRecords = false, onShowToast = null, workplaceOptions = [] }) => {
    const theme = useTheme();
    const [mode, setMode] = useState(MODE_SINGLE);
    const [selectedType, setSelectedType] = useState(EVENT_TYPE_ARRIVAL);
    const [startTime, setStartTime] = useState(dayjs().hour(8).minute(0));
    const [endTime, setEndTime] = useState(dayjs().hour(16).minute(0));
    const [workplaceId, setWorkplaceId] = useState('');
    const [error, setError] = useState(null);

    // Workplace matters only for records that create a shift
    const typeNeedsWorkplace = (type) => (
        type === EVENT_TYPE_ARRIVAL || type === EVENT_TYPE_FULL_SHIFT
    );
    const showWorkplacePicker = workplaceOptions.length > 1 && typeNeedsWorkplace(selectedType);

    // Get reason why an option is not available
    const getUnavailableReason = (type) => {
        if (hasActiveBreak && type !== EVENT_TYPE_BREAK_END && type !== EVENT_TYPE_BREAK_START && type !== EVENT_TYPE_BREAK_DURATION) {
            return 'Najprv musíte ukončiť aktívnu prestávku.';
        }
        
        if (type === EVENT_TYPE_ARRIVAL) {
            if (hasActiveRecord) {
                return 'Zmena už je začatá. Najprv ukončite aktuálnu zmenu.';
            }
            return null; // Always available
        }
        
        if (type === EVENT_TYPE_DEPARTURE) {
            if (!hasActiveRecord) {
                return 'Nemáte začatú zmenu. Najprv začnite zmenu.';
            }
            return null;
        }
        
        if (type === EVENT_TYPE_FULL_SHIFT) {
            if (hasActiveRecord) {
                return 'Zmena už je začatá. Najprv ukončite aktuálnu zmenu.';
            }
            return null; // Can add even if there are previous records
        }
        
        if (type === EVENT_TYPE_BREAK_START || type === EVENT_TYPE_BREAK_DURATION) {
            if (hasActiveBreak) {
                return 'Už máte aktívnu prestávku. Najprv ju ukončite.';
            }
            // Can add break even if shift is ended - will find or create attendance
            return null;
        }
        
        if (type === EVENT_TYPE_BREAK_END) {
            if (!hasActiveBreak) {
                return 'Nemáte aktívnu prestávku na ukončenie.';
            }
            return null;
        }
        
        return null;
    };

    // Check if option is available
    const isOptionAvailable = (type) => {
        return getUnavailableReason(type) === null;
    };

    const handleTypeClick = (type) => {
        const reason = getUnavailableReason(type);
        if (reason && onShowToast) {
            onShowToast(reason, 'warning');
            return;
        }
        setSelectedType(type);
    };

    useEffect(() => {
        if (editingEvent) {
            // Edit mode - not used for now, but kept for future
            setMode(MODE_SINGLE);
            setSelectedType(editingEvent.type);
            setStartTime(dayjs(editingEvent.time));
        } else {
            // Add mode - set defaults
            setMode(MODE_SINGLE);
            setSelectedType(EVENT_TYPE_ARRIVAL);
            const now = dayjs();
            setStartTime(now.hour(8).minute(0));
            setEndTime(now.hour(16).minute(0));
        }
        setWorkplaceId(workplaceOptions.length > 0 ? workplaceOptions[0].id : '');
        setError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingEvent, open, hasActiveRecord, hasActiveBreak, hasAnyRecords]);

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setMode(newMode);
            // Set default type for the mode
            if (newMode === MODE_RANGE) {
                if (isOptionAvailable(EVENT_TYPE_FULL_SHIFT)) {
                    setSelectedType(EVENT_TYPE_FULL_SHIFT);
                } else if (isOptionAvailable(EVENT_TYPE_BREAK_DURATION)) {
                    setSelectedType(EVENT_TYPE_BREAK_DURATION);
                }
            } else {
                if (isOptionAvailable(EVENT_TYPE_ARRIVAL)) {
                    setSelectedType(EVENT_TYPE_ARRIVAL);
                } else if (isOptionAvailable(EVENT_TYPE_DEPARTURE)) {
                    setSelectedType(EVENT_TYPE_DEPARTURE);
                } else if (isOptionAvailable(EVENT_TYPE_BREAK_START)) {
                    setSelectedType(EVENT_TYPE_BREAK_START);
                } else if (isOptionAvailable(EVENT_TYPE_BREAK_END)) {
                    setSelectedType(EVENT_TYPE_BREAK_END);
                }
            }
        }
    };

    const handleSave = () => {
        setError(null);

        // Check if selected option is available
        const reason = getUnavailableReason(selectedType);
        if (reason) {
            setError(reason);
            if (onShowToast) {
                onShowToast(reason, 'warning');
            }
            return;
        }

        // Validation
        if (mode === MODE_RANGE && startTime.isAfter(endTime)) {
            setError("Začiatok nemôže byť neskôr ako koniec.");
            return;
        }

        let startTimeStr = startTime.format('HH:mm');
        let endTimeStr = mode === MODE_RANGE ? endTime.format('HH:mm') : undefined;

        onSave(selectedType, startTimeStr, endTimeStr, typeNeedsWorkplace(selectedType) ? workplaceId : undefined);
        onClose();
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case EVENT_TYPE_ARRIVAL:
                return 'Príchod';
            case EVENT_TYPE_DEPARTURE:
                return 'Odchod';
            case EVENT_TYPE_BREAK_START:
                return 'Začiatok prestávky';
            case EVENT_TYPE_BREAK_END:
                return 'Koniec prestávky';
            case EVENT_TYPE_FULL_SHIFT:
                return 'Celá zmena';
            case EVENT_TYPE_BREAK_DURATION:
                return 'Dĺžka prestávky';
            default:
                return '';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case EVENT_TYPE_ARRIVAL:
            case EVENT_TYPE_FULL_SHIFT:
                return <AccessTimeIcon />;
            case EVENT_TYPE_DEPARTURE:
                return <LogoutIcon />;
            case EVENT_TYPE_BREAK_START:
            case EVENT_TYPE_BREAK_END:
            case EVENT_TYPE_BREAK_DURATION:
                return <FreeBreakfastIcon />;
            default:
                return <AccessTimeIcon />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case EVENT_TYPE_ARRIVAL:
            case EVENT_TYPE_FULL_SHIFT:
                return theme.palette.mode === 'dark' ? '#C8E6C9' : '#4CAF50';
            case EVENT_TYPE_DEPARTURE:
                return theme.palette.mode === 'dark' ? '#FFCDD2' : '#F44336';
            case EVENT_TYPE_BREAK_START:
            case EVENT_TYPE_BREAK_END:
            case EVENT_TYPE_BREAK_DURATION:
                return theme.palette.mode === 'dark' ? '#FFF9C4' : '#FFC107';
            default:
                return theme.palette.grey[500];
        }
    };

    // Ensure selectedDate is a dayjs object
    const dateObj = selectedDate ? dayjs(selectedDate) : dayjs();
    const formattedDate = dateObj.isValid() ? format(dateObj.toDate(), 'EEEE, d MMMM') : format(new Date(), 'EEEE, d MMMM');

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        bgcolor: theme.palette.background.paper,
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {editingEvent ? 'Upraviť čas' : 'Nový záznam'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    {/* Date Banner */}
                    <Box
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.2)'}`,
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'start',
                            gap: 1.5
                        }}
                    >
                        <CalendarTodayIcon sx={{ color: theme.palette.info.main, mt: 0.5 }} />
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: theme.palette.info.main, display: 'block', mb: 0.5 }}>
                                Aktívny dátum
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Upravujete záznamy pre <span style={{ textDecoration: 'underline', color: theme.palette.info.main }}>{formattedDate}</span>.
                            </Typography>
                        </Box>
                    </Box>

                    {editingEvent ? (
                        // Edit mode - simplified (for future use)
                        <Box>
                            <Box
                                sx={{
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)',
                                    p: 2,
                                    borderRadius: 2,
                                    mb: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 4,
                                        height: 40,
                                        borderRadius: 1,
                                        bgcolor: getTypeColor(selectedType)
                                    }}
                                />
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: 'text.secondary', display: 'block' }}>
                                        Typ udalosti
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {getTypeLabel(selectedType)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block' }}>
                                    Aktualizovať čas
                                </Typography>
                                <TimePicker
                                    value={startTime}
                                    onChange={setStartTime}
                                    ampm={false}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    fontSize: '1.5rem',
                                                    fontWeight: 'bold',
                                                    py: 2,
                                                    textAlign: 'center',
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : (
                        // Add mode - full controls
                        <>
                            {/* Mode Selector - always show */}
                            <Box sx={{ mb: 3 }}>
                                <ToggleButtonGroup
                                    value={mode}
                                    exclusive
                                    onChange={handleModeChange}
                                    fullWidth
                                    sx={{
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                        borderRadius: 2,
                                        p: 0.5,
                                        '& .MuiToggleButton-root': {
                                            flex: 1,
                                            py: 1.5,
                                            borderRadius: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&.Mui-selected': {
                                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                                color: 'text.primary',
                                            }
                                        }
                                    }}
                                >
                                    <ToggleButton value={MODE_SINGLE}>Jeden bod</ToggleButton>
                                    <ToggleButton value={MODE_RANGE}>Rozsah (Od-Do)</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Type Buttons - show all options, disable unavailable ones */}
                            <Box sx={{ mb: 3 }}>
                                {mode === MODE_SINGLE ? (
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_ARRIVAL ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_ARRIVAL)}
                                            startIcon={getTypeIcon(EVENT_TYPE_ARRIVAL)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_ARRIVAL)}
                                            sx={{
                                                flex: '1 1 calc(50% - 6px)',
                                                minWidth: 120,
                                                bgcolor: selectedType === EVENT_TYPE_ARRIVAL ? getTypeColor(EVENT_TYPE_ARRIVAL) : 'transparent',
                                                color: selectedType === EVENT_TYPE_ARRIVAL ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_ARRIVAL ? getTypeColor(EVENT_TYPE_ARRIVAL) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_ARRIVAL) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_ARRIVAL) ? (selectedType === EVENT_TYPE_ARRIVAL ? getTypeColor(EVENT_TYPE_ARRIVAL) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_ARRIVAL),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_ARRIVAL ? 'bold' : 500,
                                            }}
                                        >
                                            Príchod
                                        </Button>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_DEPARTURE ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_DEPARTURE)}
                                            startIcon={getTypeIcon(EVENT_TYPE_DEPARTURE)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_DEPARTURE)}
                                            sx={{
                                                flex: '1 1 calc(50% - 6px)',
                                                minWidth: 120,
                                                bgcolor: selectedType === EVENT_TYPE_DEPARTURE ? getTypeColor(EVENT_TYPE_DEPARTURE) : 'transparent',
                                                color: selectedType === EVENT_TYPE_DEPARTURE ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_DEPARTURE ? getTypeColor(EVENT_TYPE_DEPARTURE) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_DEPARTURE) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_DEPARTURE) ? (selectedType === EVENT_TYPE_DEPARTURE ? getTypeColor(EVENT_TYPE_DEPARTURE) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_DEPARTURE),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_DEPARTURE ? 'bold' : 500,
                                            }}
                                        >
                                            Odchod
                                        </Button>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_BREAK_START ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_BREAK_START)}
                                            startIcon={getTypeIcon(EVENT_TYPE_BREAK_START)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_BREAK_START)}
                                            sx={{
                                                flex: '1 1 calc(50% - 6px)',
                                                minWidth: 120,
                                                bgcolor: selectedType === EVENT_TYPE_BREAK_START ? getTypeColor(EVENT_TYPE_BREAK_START) : 'transparent',
                                                color: selectedType === EVENT_TYPE_BREAK_START ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_BREAK_START ? getTypeColor(EVENT_TYPE_BREAK_START) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_BREAK_START) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_BREAK_START) ? (selectedType === EVENT_TYPE_BREAK_START ? getTypeColor(EVENT_TYPE_BREAK_START) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_BREAK_START),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_BREAK_START ? 'bold' : 500,
                                            }}
                                        >
                                            Zač. prestávky
                                        </Button>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_BREAK_END ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_BREAK_END)}
                                            startIcon={getTypeIcon(EVENT_TYPE_BREAK_END)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_BREAK_END)}
                                            sx={{
                                                flex: '1 1 calc(50% - 6px)',
                                                minWidth: 120,
                                                bgcolor: selectedType === EVENT_TYPE_BREAK_END ? getTypeColor(EVENT_TYPE_BREAK_END) : 'transparent',
                                                color: selectedType === EVENT_TYPE_BREAK_END ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_BREAK_END ? getTypeColor(EVENT_TYPE_BREAK_END) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_BREAK_END) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_BREAK_END) ? (selectedType === EVENT_TYPE_BREAK_END ? getTypeColor(EVENT_TYPE_BREAK_END) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_BREAK_END),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_BREAK_END ? 'bold' : 500,
                                            }}
                                        >
                                            Kon. prestávky
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_FULL_SHIFT ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_FULL_SHIFT)}
                                            startIcon={getTypeIcon(EVENT_TYPE_FULL_SHIFT)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_FULL_SHIFT)}
                                            fullWidth
                                            sx={{
                                                bgcolor: selectedType === EVENT_TYPE_FULL_SHIFT ? getTypeColor(EVENT_TYPE_FULL_SHIFT) : 'transparent',
                                                color: selectedType === EVENT_TYPE_FULL_SHIFT ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_FULL_SHIFT ? getTypeColor(EVENT_TYPE_FULL_SHIFT) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_FULL_SHIFT) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_FULL_SHIFT) ? (selectedType === EVENT_TYPE_FULL_SHIFT ? getTypeColor(EVENT_TYPE_FULL_SHIFT) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_FULL_SHIFT),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_FULL_SHIFT ? 'bold' : 500,
                                            }}
                                        >
                                            Celá zmena
                                        </Button>
                                        <Button
                                            variant={selectedType === EVENT_TYPE_BREAK_DURATION ? 'contained' : 'outlined'}
                                            onClick={() => handleTypeClick(EVENT_TYPE_BREAK_DURATION)}
                                            startIcon={getTypeIcon(EVENT_TYPE_BREAK_DURATION)}
                                            disabled={!isOptionAvailable(EVENT_TYPE_BREAK_DURATION)}
                                            fullWidth
                                            sx={{
                                                bgcolor: selectedType === EVENT_TYPE_BREAK_DURATION ? getTypeColor(EVENT_TYPE_BREAK_DURATION) : 'transparent',
                                                color: selectedType === EVENT_TYPE_BREAK_DURATION ? '#000' : 'inherit',
                                                borderColor: selectedType === EVENT_TYPE_BREAK_DURATION ? getTypeColor(EVENT_TYPE_BREAK_DURATION) : 'divider',
                                                opacity: isOptionAvailable(EVENT_TYPE_BREAK_DURATION) ? 1 : 0.5,
                                                '&:hover': {
                                                    bgcolor: isOptionAvailable(EVENT_TYPE_BREAK_DURATION) ? (selectedType === EVENT_TYPE_BREAK_DURATION ? getTypeColor(EVENT_TYPE_BREAK_DURATION) : 'action.hover') : undefined,
                                                    borderColor: getTypeColor(EVENT_TYPE_BREAK_DURATION),
                                                },
                                                textTransform: 'none',
                                                fontWeight: selectedType === EVENT_TYPE_BREAK_DURATION ? 'bold' : 500,
                                            }}
                                        >
                                            Dĺžka prestávky
                                        </Button>
                                    </Stack>
                                )}
                            </Box>

                            {/* Time Inputs */}
                            <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block' }}>
                                        {mode === MODE_RANGE ? 'Čas začiatku' : selectedType === EVENT_TYPE_BREAK_END ? 'Čas konca prestávky' : 'Čas'}
                                    </Typography>
                                    <TimePicker
                                        value={startTime}
                                        onChange={setStartTime}
                                        ampm={false}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        fontSize: '1.125rem',
                                                        fontWeight: 'bold',
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Box>

                                {mode === MODE_RANGE && (
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block' }}>
                                            Čas konca
                                        </Typography>
                                        <TimePicker
                                            value={endTime}
                                            onChange={setEndTime}
                                            ampm={false}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '1.125rem',
                                                            fontWeight: 'bold',
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                            </Stack>

                            {/* Workplace picker */}
                            {showWorkplacePicker && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', color: 'text.secondary', mb: 1, display: 'block' }}>
                                        Pracovisko
                                    </Typography>
                                    <Select
                                        value={workplaceId}
                                        onChange={(e) => setWorkplaceId(e.target.value)}
                                        fullWidth
                                        size="small"
                                    >
                                        {workplaceOptions.map((wp) => (
                                            <MenuItem key={wp.id} value={wp.id}>{wp.name}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}
                        </>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
                        Zrušiť
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                            borderRadius: 2,
                        }}
                    >
                        {editingEvent ? 'Potvrdiť zmenu' : 'Potvrdiť záznam'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default AddEditRecordDialog;

